"use client";
import Button from "@/components/buttons";
import CloseIcon from "./assets/close-icon.svg";
import Image from "next/image";
import { FormProvider, useForm } from "react-hook-form";
import Link from "next/link";
import {
  CreateAccountFormSchema,
  createAccountFormSchema,
} from "@/utils/schemas/create-account-form-schema";

import { zodResolver } from "@hookform/resolvers/zod";

import { useZStore } from "@/utils/z-store";
import cn from "@/utils/cn";
import axios from "axios";
import { FcGoogle } from "react-icons/fc";
import toast from "react-hot-toast";
import { useState, useRef } from "react";
import { signIn, signOut } from "next-auth/react";
import SignupInputField from "../SignupInputField";
import { trackGAEvent } from "@/utils/ga";
import MailchimpConnectButton from "@/components/MailchimpConnectButton";
import TextComponent from "../TextComponent";
import TurnstileWidget, { executeTurnstile } from "@/app/_components/TurnstileWidget";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

interface CreateAccountFormModalProps {
  inviteToken?: string;
  className?: string;
}

const CreateAccountFormModal: React.FC<CreateAccountFormModalProps> = ({
  inviteToken,
  className,
}) => {
  // const pathname = usePathname();
  const { data, actions } = useZStore();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"register" | "otp">(
    data.pendingVerificationEmail ? "otp" : "register"
  );
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [lastSentTime, setLastSentTime] = useState<number | null>(null);
  const [canResend, setCanResend] = useState(true);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const methods = useForm<CreateAccountFormSchema>({
    resolver: zodResolver(createAccountFormSchema),
  });
  // const [, setFormProcessing] = useState(false);
  const isPasswordInvalid =
    methods.formState.errors.password?.message ||
    methods.formState.errors.confirmPassword?.message;

  const redirectToStripeCheckout = async () => {
    const pendingCheckout = data.pendingCheckout;
    if (!pendingCheckout) return false;

    // Free plan: skip Stripe, replace the auto-created trial with a free subscription.
    if (pendingCheckout.planName === "BASIC") {
      try {
        const response = await axios.post("/api/subscription/free");
        actions.clearPendingCheckout();
        actions.clearPaymentSuccessSessionId();
        actions.closeModal();
        window.location.href = response.data?.redirect || "/dashboard/home";
        return true;
      } catch (error) {
        console.error("Error activating free plan:", error);
      }
      return false;
    }

    try {
      const response = await axios.post("/api/subscription", {
        planName: pendingCheckout.planName,
        billingCycle: pendingCheckout.billingCycle,
      });

      if (response.data.url) {
        actions.clearPendingCheckout();
        window.location.href = response.data.url;
        return true;
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
    }
    return false;
  };

  const acceptInvitation = async (email: string, token: string) => {
    try {
      const response = await axios.post("/api/invitations/accept", {
        token,
        email,
      });

      if (response.status === 200) {
        toast.success("Invitation accepted successfully.", {
          position: "bottom-left",
        });
      } else {
        toast.error(response.data.message || "Failed to accept invitation.", {
          position: "bottom-left",
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message || "Failed to accept invitation.";
        toast.error(errorMessage, {
          position: "bottom-left",
        });
      } else {
        toast.error("An unexpected error occurred. Please try again.", {
          position: "bottom-left",
        });
      }
    }
  };

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Only allow numeric paste
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];

    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });

    setOtp(newOtp);

    // Focus last filled input or next empty
    const nextIndex = Math.min(digits.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join("");
    const email = methods.getValues("email");

    if (otpCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }

    setIsVerifying(true);

    try {
      const response = await axios.post("/api/auth/verify-otp", {
        email,
        otp: otpCode,
      });

      if (response.status === 200) {
        toast.success("Email verified successfully!");

        // Sign in the user
        const formData = methods.getValues();
        const signInResponse = await signIn("credentials", {
          email: formData.email,
          password: formData.password,
          redirect: false,
        });

        if (signInResponse?.ok) {
          actions.closeModal();
          actions.resetStore();

          // Check if there's a pending checkout and redirect to Stripe
          const hasCheckout = await redirectToStripeCheckout();

          if (!hasCheckout) {
            // No pending checkout, proceed to dashboard
            window.location.href = "/dashboard/home";
          }
        } else {
          // If sign in failed, redirect to dashboard anyway (fallback)
          window.location.href = "/dashboard/home";
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || "Invalid or expired code";
        toast.error(errorMessage);
      } else {
        toast.error("An error occurred. Please try again.");
      }
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    const email = methods.getValues("email");
    if (!email || !canResend || isResending) return;

    // Check if 60 seconds have passed since last send
    if (lastSentTime && Date.now() - lastSentTime < 60000) {
      const remainingTime = Math.ceil((60000 - (Date.now() - lastSentTime)) / 1000);
      toast.error(`Please wait ${remainingTime} seconds before resending`);
      return;
    }

    setIsResending(true);
    setCanResend(false);

    try {
      const response = await axios.post("/api/auth/resend-otp", {
        email,
      });

      if (response.status === 200) {
        toast.success("Verification code sent successfully!");
        setLastSentTime(Date.now());
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();

        // Re-enable after 60 seconds
        setTimeout(() => {
          setCanResend(true);
        }, 60000);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || "Failed to send code";
        toast.error(errorMessage);
      } else {
        toast.error("An error occurred. Please try again.");
      }
      setCanResend(true);
    } finally {
      setIsResending(false);
    }
  };

  const registerUser = async (formData: any) => {
    setLoading(true);
    if (formData.password !== formData.confirmPassword) {
      setLoading(false);
      return;
    }

    const turnstileToken = await executeTurnstile(turnstileRef);
    if (!turnstileToken) {
      toast.error(
        "Verification failed. Please disable any ad blockers and try again.",
        { position: "bottom-left" },
      );
      setLoading(false);
      return;
    }

    try {
      // Sign out any existing session first
      await signOut({ redirect: false });

      const response = await axios.post("/api/auth/register", {
        ...formData,
        turnstileToken,
      });

      if (response.status === 200) {
        // Set pending verification email
        actions.setPendingVerificationEmail(formData.email);

        // Switch to OTP step
        setStep("otp");

        toast.success("Please verify your email to continue", {
          position: "bottom-left",
        });

        // Track successful sign-up event
        trackGAEvent("sign-up", {
          method: "credentials",
        });
        // Canonical funnel event (Tracking tab): account created.
        trackGAEvent("signup_completed", { method: "credentials" });

        if (inviteToken) {
          await acceptInvitation(formData.email, inviteToken);
        }
      }
    } catch (error) {
      // DO NOT close modal or reset store on error
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message || "Failed to sign up.";

        // Check if it's an email or username error and set field-specific errors
        if (errorMessage.includes("Email already exists")) {
          methods.setError("email", {
            type: "manual",
            message: "Email already exists. Please sign in.",
          });
        } else if (errorMessage.includes("Username already taken")) {
          methods.setError("username", {
            type: "manual",
            message: "Username already taken. Please choose another.",
          });
        } else {
          // Generic error toast for other errors
          toast.error(errorMessage, {
            position: "bottom-left",
          });
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.", {
          position: "bottom-left",
        });
      }
    } finally {
      turnstileRef.current?.reset();
      setLoading(false);
    }
  };
  const handleGoogleSignIn = async () => {
    try {
      // Sign out any existing session first
      await signOut({ redirect: false });
      actions.closeModal();
      actions.resetStore();

      if (inviteToken) {
        localStorage.setItem("inviteToken", inviteToken);
      }

      // Store pending checkout in localStorage for Google OAuth callback
      if (data.pendingCheckout) {
        localStorage.setItem(
          "pendingCheckout",
          JSON.stringify(data.pendingCheckout),
        );
      }

      // Track Google sign-up initiation (actual success will be tracked on callback)
      trackGAEvent("sign-up", {
        method: "google",
      });

      await signIn("google", {
        callbackUrl: "/dashboard/home",
      });
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <FormProvider {...methods}>
      <div
        className={cn(
          "relative flex w-[262px] h-[693px] items-center justify-center overflow-hidden rounded-[20px]  bg-white md:h-[418px] md:w-[550px] xl:h-[596px] xl:w-[1067px]",
        )}
      >
        {/* side image */}
        <div className="relative hidden h-full w-[35%] overflow-hidden bg-kd-lte-blue text-white md:block">
          <div className="absolute -right-36 -top-36 -z-0 h-[227.25px] w-[229.5px] rotate-90 rounded-full bg-gradient-to-tl  from-white/40 via-transparent to-white/0 md:-right-28 md:-top-40 xl:-right-36 xl:-top-52 xl:h-[303px] xl:w-[306px]"></div>

          <div
            className={cn(
              "absolute -bottom-52 -left-44 -z-0 rounded-full bg-gradient-to-bl from-white/30 via-transparent to-white/0 md:-bottom-[120px] md:-left-[70px] md:size-[181.5px] xl:-bottom-[168px] xl:-left-20 xl:size-[242px]",
            )}
          ></div>
          <div className=" flex h-full justify-center">
            <div className="md:pt-[135px] xl:pt-[228px]">
              <TextComponent type="h3" className="flex px-3 text-center md:!text-[23px] md:!leading-[30px] xl:!text-[35px] xl:!leading-[40px]">
                {step === "otp" ? "Have an account?" : "Welcome Back"}
              </TextComponent>
              <div className=" flex  justify-center md:pb-[21px] md:pt-[12px] xl:pb-[26px] xl:pt-[13px]">
                <div className=" text-center text-[#D1D1D6] md:h-[48px] md:w-[158px] md:text-[10.5px] xl:h-[50px] xl:w-[257px] xl:text-sm">
                  Do you already have an account? Please login with your
                  personal info
                </div>
              </div>
              <div className=" flex justify-center">
                <Button
                  variant="inverse"
                  onClick={() => actions.openModal({ modalName: "sign-in" })}
                  className={cn(
                    "md:h-[30px] md:w-[80.25px] whitespace-nowrap rounded-lg px-4 md: text-[12px] font-semibold xl:h-10 xl:w-[107px] xl:text-base ",
                  )}
                  disabled={loading}
                >
                  Sign in
                </Button>
              </div>
              <div className="flex justify-center md:pb-6 md:pt-[67.5px] xl:pb-10 xl:pt-[133px]">
                <div className="text-center text-[#D1D1D6] md:w-[180px] md:text-[8px] xl:w-[280px] xl:text-[12px]">
                  By signing up, you agree to Email Audit Engine&apos;s{" "}
                  <Link
                    href="https://emailauditengine.com/resources/terms-and-conditions"
                    className="hover:underline"
                    target="_blank"
                  >
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="https://emailauditengine.com/resources/privacy-policy"
                    className="hover:underline"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                  .
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "h-full w-full bg-white md:pb-[28px] pb-[10px] text-kd-secondary-2 md:w-[65%] pt-[42px] md:pt-[42px] xl:pb-[56px] xl:pt-[69px]",
          )}
        >
          {step === "register" ? (
            <>
              <div className=" flex justify-center px-[29px] text-center text-[23px] font-semibold text-kd-secondary-1 md:px-0 xl:text-[35px]">
                Create A Free Account
              </div>

              {/* signup with google and apple */}
              <div className="flex justify-center gap-3 pb-3 pt-[10px] md:pb-[19.25px] md:pt-[16px] xl:pb-[26px] xl:pt-[36px] ">
                <Button
                  onClick={handleGoogleSignIn}
                  className="flex h-[44.77px] w-[47.75px] items-center justify-center rounded-xl border border-kd-secondary-6 bg-[#FAFBFC]  text-kd-secondary-2 xl:h-[60px] xl:w-[64px] xl:rounded-[16px]"
                >
                  <FcGoogle className="size-[18px] xl:size-6" />
                </Button>
                <MailchimpConnectButton
                  mode="auth"
                  size="lg"
                  className="h-[44.77px] w-[47.75px] xl:h-[60px] xl:w-[64px] rounded-xl xl:rounded-[16px] border border-kd-secondary-6"
                  onAuthSuccess={({ loginToken, isNewUser }) => {
                    const url = `/auth/mailchimp/callback?token=${encodeURIComponent(loginToken)}&fromMailchimp=true${isNewUser ? "&new=true" : ""}`;
                    window.location.href = url;
                  }}
                  onError={(errorCode) => {
                    if (errorCode === "popup_closed") return;
                    console.error("Mailchimp sign-up failed:", errorCode);
                  }}
                />
              </div>
              <div className=" flex items-center justify-center space-x-2 xl:space-x-5  ">
                <div className="h-px w-1/5 bg-kd-secondary-6"></div>
                <div className=" text-[10.5px] text-kd-secondary-3 xl:text-sm">
                  or sign up with email
                </div>
                <div className="h-px w-1/5 bg-kd-secondary-6"></div>
              </div>
              <form onSubmit={methods.handleSubmit(registerUser)}>
                <div className=" flex justify-center pt-2 md:pt-[18px] xl:pt-[25px]">
                  <div>
                    <SignupInputField
                      name="email"
                      type="email"
                      autoComplete="username"
                      className="mb-2 flex h-[40px] w-[204px] rounded-[12px] px-[15px] font-normal md:mb-[8px] md:h-[40px] md:w-[280px] md:rounded-xl md:px-[18px] xl:mb-[16px] xl:h-[60px] xl:w-[592px] xl:rounded-2xl xl:px-[24px] "
                      placeholder="Email address"
                      isRequired={true}
                      errorTextPosition="right"
                    />

                    <SignupInputField
                      name="username"
                      autoComplete="nickname"
                      className="mb-2 flex h-[40px] w-[204px] rounded-[12px] px-[15px] font-normal md:mb-[8px] md:h-[40px] md:w-[280px] md:rounded-xl md:px-[18px] xl:mb-[16px] xl:h-[60px] xl:w-[592px] xl:rounded-2xl"
                      placeholder="Username"
                      isRequired={true}
                      errorTextPosition="right"
                    />

                    <div className="flex flex-col md:flex-row md:space-x-2 xl:space-x-5">
                      <SignupInputField
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        className="mb-2 flex h-[40px] w-[204px] rounded-[12px] px-[15px] font-normal tracking-[4px] placeholder:tracking-normal md:mb-[8px] md:h-[40px] md:w-[136px] md:rounded-xl md:px-[18px] xl:mb-[16px] xl:h-[60px] xl:w-[286px] xl:rounded-2xl"
                        placeholder="Password"
                        isRequired={true}
                        errorTextPosition="right"
                      />

                      <SignupInputField
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        className=" mb-2 flex h-[40px] w-[204px] rounded-[12px] px-[15px] font-normal tracking-[4px] placeholder:tracking-normal md:mb-[8px] md:h-[40px] md:w-[136px] md:rounded-xl md:px-[18px] xl:mb-[16px] xl:h-[60px] xl:w-[286px] xl:rounded-2xl"
                        placeholder="Confirm Password"
                        isRequired={true}
                        errorTextPosition="right"
                      />
                    </div>
                  </div>
                </div>
                <TurnstileWidget ref={turnstileRef} />
                <div className="flex items-center justify-center pt-1 md:pt-5 xl:pt-1">
                  <Button
                    id="submit-signup"
                    type="submit"
                    variant="primary"
                    className={cn(
                      "md:h-[30px] md:w-[80.25px] whitespace-nowrap rounded-lg px-4 text-[12px] font-semibold leading-5 xl:h-10 xl:w-[107px] xl:text-base ",
                    )}
                    disabled={loading}
                  >
                    Sign up
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div id="successful-signup" className="flex flex-col items-center justify-center h-full px-8">
              <div className="text-center text-[23px] font-semibold text-kd-secondary-1 xl:text-[35px]">
                Account Verification
              </div>
              <div className="mt-4 text-center text-[10.5px] text-kd-secondary-3 xl:text-sm">
                The verification code has been sent to your email
              </div>
              <div className="mt-2 flex items-center justify-center gap-2 text-center text-[10.5px] font-medium text-kd-lte-blue xl:text-sm">
                <span>{methods.getValues("email")}</span>
                <button
                  type="button"
                  onClick={() => {
                    setStep("register");
                    setOtp(["", "", "", "", "", ""]);
                    actions.setPendingVerificationEmail(null);
                  }}
                  className="text-[9px] xl:text-xs text-kd-secondary-3 underline hover:text-kd-secondary-1"
                >
                  Change
                </button>
              </div>

              <div className="mt-8 md:mt-10 w-full">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="h-px w-16 bg-gray-200"></div>
                  <div className="text-center text-[10.5px] text-kd-secondary-3 xl:text-sm">
                    Enter your code here
                  </div>
                  <div className="h-px w-16 bg-gray-200"></div>
                </div>

                <div className="flex justify-center gap-3 md:gap-4 xl:gap-5">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className={cn(
                        "w-[45px] h-[55px] md:w-[55px] md:h-[65px] xl:w-[65px] xl:h-[75px]",
                        "text-center text-[24px] md:text-[28px] xl:text-[32px] font-bold",
                        "border rounded-xl md:rounded-2xl",
                        "focus:outline-none focus:ring-2 focus:ring-kd-lte-blue",
                        "transition-all duration-200",
                        digit
                          ? "border-kd-lte-blue text-kd-lte-blue bg-blue-50"
                          : "border-gray-200 text-kd-secondary-2 hover:border-kd-lte-blue/50"
                      )}
                      disabled={isVerifying}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-6 md:mt-8 text-center text-[10.5px] text-kd-secondary-3 xl:text-sm">
                Did not receive OTP?{" "}
                <button
                  onClick={handleResendOTP}
                  disabled={isResending || !canResend}
                  className={cn(
                    "text-kd-lte-blue font-medium hover:underline",
                    (isResending || !canResend) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {isResending ? "Sending..." : "Resend"}
                </button>
              </div>

              <div className="mt-8 md:mt-10 flex justify-center">
                <Button
                  type="button"
                  variant="primary"
                  className="font-semibold text-white h-[40px] w-[140px] md:h-[45px] md:w-[160px] xl:h-[50px] xl:w-[180px] rounded-xl text-[14px] md:text-[15px] xl:text-[16px]"
                  onClick={handleVerifyOTP}
                  disabled={isVerifying || otp.some((d) => !d)}
                >
                  {isVerifying ? "Verifying..." : "Verify"}
                </Button>
              </div>
            </div>
          )}

          <div
            className={cn(
              "relative mt-3 size-full overflow-hidden bg-kd-lte-blue text-white md:hidden",
            )}
          >
            <div className="absolute -right-36 -top-36 -z-0 h-[227.25px] w-[229.5px]  rotate-90 rounded-full bg-gradient-to-tl  from-white/40 via-transparent to-white/0 xl:-right-36 xl:-top-52"></div>

            <div className=" flex h-full justify-center">
              <div className=" px-[29px] pt-[42px]">
                <TextComponent type="h3" className="flex justify-center px-3 text-center !text-lg">
                  Welcome Back
                </TextComponent>
                <div className=" flex  justify-center py-2">
                  <div className=" w-[170px] text-center text-xs text-[#D1D1D6]">
                    Do you already have an account? Please login with your
                    personal info
                  </div>
                </div>
                <div className=" flex justify-center">
                  <Button
                    variant="inverse"
                    onClick={() => actions.openModal({ modalName: "sign-in" })}
                    className={cn(
                      "md:h-[30px] md:w-[80.25px] whitespace-nowrap rounded-lg px-4 md: text-[12px] font-semibold xl:h-10 xl:w-[107px] xl:text-base ",
                    )}
                    disabled={loading}
                  >
                    Sign in
                  </Button>
                </div>
                <div className="flex justify-center pb-6 pt-4">
                  <div className="text-center text-[8px] text-[#D1D1D6]">
                    By signing up, you agree to Email Audit Engine&apos;s{" "}
                    <Link
                      href="https://emailauditengine.com/resources/terms-and-conditions"
                      className="hover:underline"
                      target="_blank"
                    >
                      Terms and Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="https://emailauditengine.com/resources/privacy-policy"
                      className="hover:underline"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* close icon */}
        <div className="" onClick={() => actions.closeModal()}>
          <Image
            src={CloseIcon}
            alt="close icon"
            className="absolute right-5 top-5 cursor-pointer hover:opacity-60"
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default CreateAccountFormModal;
